import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import OnScroll from './OnScroll';
import SMHeaderContext from './SMHeaderContext';
import { Addon } from './components/Addon';
import UserDropDown from './components/UserDropDown';
import HeaderLogo from './components/HeaderLogo';
import MomentiveHeaderLogo from './components/MomentiveHeaderLogo';
import { MobileDrawer } from './components/Drawer/Mobile';
import DesktopDrawer from './components/Drawer/Desktop';

import NavBarAnon from './anonymous/Desktop';
import NavBarAuth from './authenticated/Desktop';
import MobileAuth from './authenticated/Mobile';
import MobileAnon from './anonymous/Mobile';
import { ValidMenuNameList } from './config/linkInfo';
import { MobileTabletScreen, DesktopScreen } from '../..';
import { BRAND, HEADER_COLOR, HEADER_VARIANT } from './enums';

import './header.scss';
import './utils.scss';

const brandLogoMapper = Object.freeze({
  [BRAND.MOMENTIVE]: MomentiveHeaderLogo,
  [BRAND.SURVEYMONKEY]: HeaderLogo,
});

function getDefaultLogoByBrand(brand) {
  return brandLogoMapper[brand] || HeaderLogo;
}

const SMHeader = ({
  isUserAuthenticated,
  showCreateSurveyButton,
  color,
  lang,
  variant,
  isEUDC,
  actionFlow,
  user,
  isAnonHeaderSticky,
  isMRX,
  excludeMenuItems,
  showLoginButton,
  showSignupButton,
  displayLICTA,
  showDrawer,
  alwaysUseAnonymousNav,
  Logo,
  brand,
}) => {
  const LogoComponent = Logo || getDefaultLogoByBrand(brand);
  const auth = isUserAuthenticated ? 'auth' : 'anon';
  const headerColor = isUserAuthenticated ? 'primary' : color;
  const headerVariant = isUserAuthenticated ? 'default' : variant;

  const navBarAnonVariant =
    !isUserAuthenticated && ['default', 'slim'].includes(variant);
  const navBarAuthVariant = isUserAuthenticated && variant === 'default';

  return (
    <SMHeaderContext.Provider
      value={{
        showCreateSurveyButton,
        isUserAuthenticated,
        lang,
        isEUDC,
        actionFlow,
        user,
        isMRX,
        excludeMenuItems,
      }}
    >
      <>
        <OnScroll
          render={(x, y) => {
            const headerColorAnon = y > 0 ? 'light' : color;
            const smHeaderColor = isUserAuthenticated
              ? headerColor
              : headerColorAnon;

            const defaultColor =
              headerColorAnon === 'transparent' ||
              headerColorAnon === 'primary';

            const logoColor = defaultColor ? 'alt' : 'primary';
            const mobileMenuColor = defaultColor ? 'alt' : 'secondary';
            const addonLogInColor = defaultColor ? 'alt' : 'secondary';
            const navBarColor = defaultColor ? 'alt' : 'secondary';
            const addonSignUpColor = defaultColor ? 'alt' : 'upgrade';
            const addonSignUpVariant = defaultColor ? 'ghost' : 'default';

            const classNames = classnames(
              `sm-header--${auth}`,
              `sm-header--${smHeaderColor}`,
              `sm-header--${headerVariant}`,
              { 'sm-header--en': lang === 'en' },
              {
                'sm-header--sticky': isAnonHeaderSticky && !isUserAuthenticated,
              }
            );

            const logoComponentProps = { isUserAuthenticated };
            if (brand === BRAND.MOMENTIVE) {
              logoComponentProps.isContrast =
                smHeaderColor === HEADER_COLOR.LIGHT;
            } else {
              logoComponentProps.variant = variant;
              logoComponentProps.color = logoColor;
            }

            return (
              <div className={classNames}>
                <MobileTabletScreen>
                  {isUserAuthenticated ? (
                    <MobileDrawer
                      menuColor={navBarColor}
                      data-testid="Header__MobileDrawer--Auth"
                    >
                      <MobileAuth />
                    </MobileDrawer>
                  ) : (
                    <MobileDrawer
                      menuColor={mobileMenuColor}
                      data-testid="Header__MobileDrawer--Anon"
                    >
                      <MobileAnon />
                    </MobileDrawer>
                  )}
                </MobileTabletScreen>
                <LogoComponent {...logoComponentProps} />
                {isUserAuthenticated && (
                  <div
                    style={{ order: 4 }}
                    className="sm-header--menu-drop-down--container"
                  >
                    <UserDropDown lang={lang} isEUDC={isEUDC} />
                  </div>
                )}
                {/* eslint-disable-next-line no-nested-ternary */}
                {navBarAnonVariant || isMRX || alwaysUseAnonymousNav ? (
                  <DesktopScreen>
                    <NavBarAnon color={navBarColor} lang={lang} />
                  </DesktopScreen>
                ) : navBarAuthVariant && !isMRX && !alwaysUseAnonymousNav ? (
                  <DesktopScreen>
                    <NavBarAuth user={user} />
                  </DesktopScreen>
                ) : null}
                <Addon
                  addonSignUpColor={addonSignUpColor}
                  addonLogInColor={addonLogInColor}
                  addonSignUpVariant={addonSignUpVariant}
                  variant={variant}
                  isUserAuthenticated={isUserAuthenticated}
                  showCreateSurveyButton={showCreateSurveyButton}
                  showLoginButton={showLoginButton}
                  showSignupButton={showSignupButton}
                  displayLICTA={displayLICTA}
                />
                {isUserAuthenticated && showDrawer && (
                  <DesktopScreen>
                    <DesktopDrawer />
                  </DesktopScreen>
                )}
              </div>
            );
          }}
        />
      </>
    </SMHeaderContext.Provider>
  );
};

SMHeader.propTypes = {
  isAnonHeaderSticky: PropTypes.bool,
  color: PropTypes.oneOf(Object.values(HEADER_COLOR)),
  isUserAuthenticated: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  showCreateSurveyButton: PropTypes.bool,
  lang: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(Object.values(HEADER_VARIANT)),
  isEUDC: PropTypes.bool.isRequired,
  actionFlow: PropTypes.string,
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isMRX: PropTypes.bool,
  displayLICTA: PropTypes.bool,
  showDrawer: PropTypes.bool,
  excludeMenuItems: PropTypes.arrayOf(PropTypes.oneOf(ValidMenuNameList)),
  showLoginButton: PropTypes.bool,
  showSignupButton: PropTypes.bool,
  alwaysUseAnonymousNav: PropTypes.bool,
  Logo: PropTypes.func,
  brand: PropTypes.oneOf(Object.values(BRAND)),
};

SMHeader.defaultProps = {
  isAnonHeaderSticky: true,
  color: 'primary',
  isUserAuthenticated: false,
  showCreateSurveyButton: true,
  displayLICTA: true,
  showDrawer: true,
  variant: 'default',
  actionFlow: 'header',
  isMRX: false,
  excludeMenuItems: [],
  showLoginButton: true,
  showSignupButton: true,
  alwaysUseAnonymousNav: false,
  Logo: undefined,
  brand: BRAND.SURVEYMONKEY,
};

export default SMHeader;
